import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Collapse, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';

import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { REMOTE_DEVICES } from 'enum/thing';

import './styles.scss';

const getTlsVpnLink = (token) => `${process.env.REACT_APP_API_URL}/v2/tlsvpn/client/ubuntu-18.04?_token=${token}`;
const getOpenVpnLink = (token) => `${process.env.REACT_APP_API_URL}/v2/tlsvpn/config/openvpn-udp?_token=${token}`;
const panelSx = {
  mb: 2,
  border: '1px solid #ddd',
  borderRadius: '4px',
  boxShadow: '0 1px 1px rgba(0, 0, 0, 0.05)',
};
const panelHeadingSx = {
  px: '15px',
  py: '10px',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #ddd',
  borderTopLeftRadius: '3px',
  borderTopRightRadius: '3px',
};
const panelBodySx = {
  p: '15px',
};

const ThingDevices = ({ data, token = '' }) => {
  const [searchText, setSearchText] = useState('');
  const [isCollapsed, setIsCollapsed] = useState({
    'Mobile Devices': false,
    'Personal Computers and Servers': false,
    'Acreto Connect Client Installer': false,
  });
  const [copiedOption, setCopiedOption] = useState(null);
  const copyTimeoutRef = useRef(null);

  const { copy } = useCopyToClipboard();

  const vpnLink = useMemo(
    () => ({
      tlsvpn: getTlsVpnLink(token),
      openvpn: getOpenVpnLink(token),
    }),
    [token],
  );

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchChange = (event) => {
    event.preventDefault();
    const { value } = event.target;
    setSearchText(value);
    setIsCollapsed(
      Object.keys(isCollapsed).reduce((acc, key) => {
        acc[key] = !!value;
        return acc;
      }, {}),
    );
  };

  const handleExpand = (title) => (event) => {
    event.preventDefault();
    setIsCollapsed((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const handleDownload = async (vpn) => {
    try {
      const res = await fetch(vpnLink[vpn]);
      if (!res.ok) throw new Error('Download failed');

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${data.name}.ovpn`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const handleCopy = (label, value) => {
    if (!token) return;
    copy(vpnLink[value]);
    setCopiedOption(label);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => setCopiedOption(null), 3000);
  };

  const searchOptions = (label) => !searchText || label.toLowerCase().includes(searchText.toLowerCase());

  return (
    <div className="panel__devices">
      <Box className="panel" sx={panelSx}>
        <Box className="panel-heading" sx={panelHeadingSx}>
          <input placeholder="Search" onChange={handleSearchChange} />
          <SearchIcon className="search-icon" />
        </Box>
        <Box className="panel-body" sx={panelBodySx}>
          {REMOTE_DEVICES.map(({ title, options }) => (
            <div key={`key-${title}`} className="download-list">
              <span className="download-list__label" onClick={handleExpand(title)}>
                {isCollapsed[title] ? <ExpandMoreIcon /> : <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />}
                {title}
              </span>
              <Collapse in={isCollapsed[title]}>
                <div className="download-list__collapse">
                  {options.map(({ label, value }) => {
                    if (!searchOptions(label)) return null;
                    return (
                      <div key={`thing-config-${label}`} className="download__option">
                        <span>{label}</span>
                        <div className="download__functions">
                          <Tooltip
                            slotProps={{ tooltip: { sx: { fontSize: 12, marginTop: '0 !important' } } }}
                            title="Download"
                          >
                            <IconButton size="small" disabled={!token} onClick={() => handleDownload(value)}>
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            slotProps={{ tooltip: { sx: { fontSize: 12, marginTop: '0 !important' } } }}
                            title={copiedOption === label ? '' : 'Copy URL'}
                          >
                            <IconButton size="small" disabled={!token} onClick={() => handleCopy(label, value)}>
                              <ContentCopyIcon id="copy-icon" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            open={copiedOption === label}
                            title="Copied"
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            slotProps={{
                              tooltip: { sx: { fontSize: 12 } },
                              popper: {
                                disablePortal: true,
                              },
                            }}
                          >
                            <Box sx={{ position: 'absolute', width: 0, height: 0, right: 12 }} />
                          </Tooltip>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Collapse>
            </div>
          ))}
        </Box>
      </Box>
    </div>
  );
};

ThingDevices.propTypes = {
  data: PropTypes.object.isRequired,
  token: PropTypes.string,
};

export default ThingDevices;
